import { useCallback, useEffect, useState } from "react";

import { ContentManager } from "./ContentManager.jsx";
import { CONTENT_CONFIGS } from "./contentConfigs.js";

function gbp(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(n || 0);
}

const POS = "var(--green)";
const NEG = "#b42318";

function Stat({ n, label, color }) {
  return (
    <div className="stat">
      <div className="n" style={color ? { color } : {}}>
        {n}
      </div>
      <div className="l">{label}</div>
    </div>
  );
}

function BudgetVsActual({ chapterId, canManage }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ category: "", kind: "expense", amount: "" });
  const [error, setError] = useState("");

  const load = useCallback(() => {
    fetch(`/api/chapters/${chapterId}/finance/budget?year=${year}`, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => d && setData(d));
  }, [chapterId, year]);
  useEffect(load, [load]);

  async function save(category, kind, amount) {
    setError("");
    const res = await fetch(`/api/chapters/${chapterId}/finance/budget`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ year, category, kind, amount }),
    });
    if (!res.ok) setError((await res.json().catch(() => ({}))).error || "Could not save.");
    load();
  }

  async function addLine(e) {
    e.preventDefault();
    if (!form.category.trim() || !form.amount) return;
    await save(form.category.trim(), form.kind, form.amount);
    setForm({ category: "", kind: "expense", amount: "" });
  }

  if (data === null) return null;

  return (
    <div className="form-card" style={{ marginTop: 16 }}>
      <div className="section-head" style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Budget vs actual</h3>
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button className="btn-ghost" type="button" onClick={() => setYear(year - 1)}>←</button>
          <strong>{year}</strong>
          <button className="btn-ghost" type="button" onClick={() => setYear(year + 1)}>→</button>
        </span>
      </div>

      {data.rows.length ? (
        <table className="tbl">
          <thead>
            <tr><th>Category</th><th>Type</th><th style={{ textAlign: "right" }}>Budget</th><th style={{ textAlign: "right" }}>Actual</th><th style={{ width: "26%" }}>Used</th><th style={{ textAlign: "right" }}>Variance</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const pct = r.budget ? Math.min(100, (r.actual / r.budget) * 100) : 0;
              const over = r.budget !== null && r.actual > r.budget;
              const varianceGood = r.variance !== null && (r.kind === "income" ? r.variance >= 0 : r.variance <= 0);
              return (
                <tr key={`${r.kind}:${r.category}`}>
                  <td>{r.category}</td>
                  <td className="muted" style={{ fontSize: 13 }}>{r.kind}</td>
                  <td style={{ textAlign: "right" }}>
                    {canManage ? (
                      <input
                        type="number"
                        step="10"
                        name={`budget-${r.kind}-${r.category}`}
                        defaultValue={r.budget ?? ""}
                        placeholder="—"
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== String(r.budget ?? "")) save(r.category, r.kind, v || 0);
                        }}
                        style={{ width: 90, textAlign: "right", padding: "4px 8px" }}
                      />
                    ) : r.budget !== null ? (
                      gbp(r.budget)
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>{gbp(r.actual)}</td>
                  <td>
                    {r.budget !== null && (
                      <div className="bar">
                        <span style={{ width: `${pct}%`, background: over ? NEG : undefined }} />
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "right", color: r.variance === null ? undefined : varianceGood ? POS : NEG, fontWeight: 600, fontSize: 13 }}>
                    {r.variance === null ? <span className="muted">unbudgeted</span> : gbp(r.variance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="empty">No budget lines or ledger activity for {year} yet.</p>
      )}

      {canManage && (
        <form onSubmit={addLine} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <input name="budget-category" placeholder="Category (e.g. Venue hire)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ width: 200 }} />
          <select name="budget-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="expense">expense</option>
            <option value="income">income</option>
          </select>
          <input name="budget-amount" type="number" step="10" placeholder="Annual budget (£)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ width: 150 }} />
          <button className="btn-ghost" type="submit">Set budget line</button>
          {error && <span className="err">{error}</span>}
        </form>
      )}
    </div>
  );
}

export function Finance({ chapterId, canManage }) {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(
    (f, t) => {
      const qs = f && t ? `?from=${f}&to=${t}` : "";
      fetch(`/api/chapters/${chapterId}/finance/summary${qs}`, {
        credentials: "same-origin",
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((d) => d && setData(d));
    },
    [chapterId]
  );
  useEffect(() => load(from, to), [load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === null) return <p className="muted">Loading finances…</p>;

  const maxCat = Math.max(
    1,
    ...data.by_category.map((c) => c.income + c.expense)
  );

  return (
    <div className="section">
      <div className="stat-grid">
        <Stat n={gbp(data.balance)} label="Balance" color={data.balance < 0 ? NEG : POS} />
        <Stat n={gbp(data.income_total)} label="Income (all time)" />
        <Stat n={gbp(data.expense_total)} label="Expenses (all time)" />
        <Stat n={data.unreconciled} label="Unreconciled" />
      </div>

      <div className="form-card" style={{ marginTop: 16 }}>
        <div className="section-head" style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Period</h3>
          <a
            className="btn-ghost"
            href={`/api/chapters/${chapterId}/finance/export`}
            style={{ textDecoration: "none" }}
          >
            Export CSV (Sage / Xero)
          </a>
        </div>
        <div
          style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 14 }}
        >
          <label className="field">
            From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button className="btn-ghost" type="button" onClick={() => load(from, to)}>
            Apply
          </button>
          {(from || to) && (
            <button
              className="btn-ghost"
              type="button"
              onClick={() => {
                setFrom("");
                setTo("");
                load("", "");
              }}
            >
              Clear
            </button>
          )}
        </div>
        <div className="stat-grid">
          <Stat n={gbp(data.period.income)} label="Income" />
          <Stat n={gbp(data.period.expense)} label="Expenses" />
          <Stat n={gbp(data.period.net)} label="Net" color={data.period.net < 0 ? NEG : POS} />
        </div>

        {data.by_category.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="l" style={{ color: "var(--muted)", fontSize: 13, marginBottom: 8 }}>
              By category
            </div>
            {data.by_category.map((c) => (
              <div
                key={c.category}
                style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}
              >
                <span style={{ width: 130, fontSize: 13 }}>{c.category}</span>
                <div className="bar" style={{ flex: 1 }}>
                  <span style={{ width: `${((c.income + c.expense) / maxCat) * 100}%` }} />
                </div>
                <span style={{ width: 150, textAlign: "right", fontSize: 13 }}>
                  {c.income > 0 && <span style={{ color: POS }}>+{gbp(c.income)} </span>}
                  {c.expense > 0 && <span style={{ color: NEG }}>−{gbp(c.expense)}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <BudgetVsActual chapterId={chapterId} canManage={canManage} />

      <div style={{ marginTop: 20 }}>
        <ContentManager
          chapterId={chapterId}
          config={CONTENT_CONFIGS.finance}
          canManage={canManage}
          onChange={() => load(from, to)}
        />
      </div>
    </div>
  );
}
